<div class="flex flex-wrap items-center gap-3">
  <a
    hlmBtn
    variant="ghost"
    size="sm"
    routerLink="/settings"
    [queryParams]="{ tab: 'voucher-design' }">
    <ng-icon name="lucideArrowLeft" />
    Einstellungen
  </a>

  <!-- The design's name: a title that turns into a field on click, like a document title. -->
  @if (editingName()) {
    <input
      #nameField
      hlmInput
      class="max-w-xs"
      type="text"
      maxlength="100"
      aria-label="Name des Designs"
      [value]="name()"
      (blur)="onNameCommit($event)"
      (keydown.enter)="onNameCommit($event)"
      (keydown.escape)="cancelNameEdit()" />
  } @else {
    <button
      hlmBtn
      variant="ghost"
      size="sm"
      type="button"
      class="group max-w-xs gap-2 px-2 text-base font-semibold"
      [hlmTooltip]="'Klicken, um das Design umzubenennen'"
      aria-label="Design umbenennen"
      (click)="startNameEdit()">
      <span class="truncate">{{ name() }}</span>
      <ng-icon
        name="lucidePencil"
        class="text-muted-foreground opacity-60 group-hover:opacity-100" />
    </button>
  }

  <span hlmBadge variant="secondary">{{ typeLabel() }}</span>
  @if (isActive()) {
    <span hlmBadge>In Verwendung</span>
  } @else if (dirty()) {
    <span hlmBadge variant="outline">Ungespeichert</span>
  } @else {
    <span hlmBadge variant="secondary">Gespeichert</span>
  }
  <!-- Issues: a counter of fixed size; the list opens on click. -->
  @if (errorCount() > 0 || warningCount() > 0) {
    <button
      hlmBtn
      size="sm"
      type="button"
      class="h-7 gap-1.5 px-2"
      [variant]="errorCount() > 0 ? 'destructive' : 'outline'"
      [hlmDropdownMenuTrigger]="issuesMenu"
      align="start"
      [attr.aria-label]="errorCount() + ' Fehler, ' + warningCount() + ' Hinweise anzeigen'">
      @if (errorCount() > 0) {
        <ng-icon name="lucideTriangleAlert" />
        {{ errorCount() }} offen
      }
      @if (warningCount() > 0) {
        <ng-icon name="lucideInfo" [class.ms-1]="errorCount() > 0" />
        {{ warningCount() }} {{ warningCount() === 1 ? 'Hinweis' : 'Hinweise' }}
      }
    </button>
  } @else {
    <span hlmBadge variant="outline" class="gap-1 text-green-700">
      <ng-icon name="lucideCircleCheckBig" />
      Alles in Ordnung
    </span>
  }

  <div class="ml-auto flex flex-wrap items-center gap-2">
    <div class="hidden items-center gap-1 lg:flex" role="group" aria-label="Seitenleisten">
      <button
        hlmBtn
        size="sm"
        type="button"
        [variant]="paletteOpen() ? 'outline' : 'secondary'"
        aria-label="Elemente ein- oder ausblenden"
        [hlmTooltip]="paletteOpen() ? 'Elemente ausblenden' : 'Elemente einblenden'"
        (click)="paletteToggle.emit()">
        <ng-icon name="lucidePanelLeft" />
      </button>
      <button
        hlmBtn
        size="sm"
        type="button"
        [variant]="inspectorOpen() ? 'outline' : 'secondary'"
        aria-label="Eigenschaften ein- oder ausblenden"
        [hlmTooltip]="inspectorOpen() ? 'Eigenschaften ausblenden' : 'Eigenschaften einblenden'"
        (click)="inspectorToggle.emit()">
        <ng-icon name="lucidePanelRight" />
      </button>
    </div>
    <div class="flex items-center gap-1" role="group" aria-label="Rückgängig und Wiederholen">
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        aria-label="Rückgängig"
        [disabled]="!canUndo()"
        [hlmTooltip]="'Rückgängig (⌘Z / Strg+Z)'"
        (click)="undo.emit()">
        @if (restoring()) {
          <hlm-spinner size="sm" />
        } @else {
          <ng-icon name="lucideUndo2" />
        }
      </button>
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        aria-label="Wiederholen"
        [disabled]="!canRedo()"
        [hlmTooltip]="'Wiederholen (⇧⌘Z / Strg+Y)'"
        (click)="redo.emit()">
        <ng-icon name="lucideRedo2" />
      </button>
    </div>
    <div class="flex items-center gap-1" role="group" aria-label="Zoom">
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        aria-label="Verkleinern"
        (click)="zoomChange.emit(zoom() / 1.25)">
        <ng-icon name="lucideZoomOut" />
      </button>
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        aria-label="Einpassen"
        [hlmTooltip]="'Seite einpassen'"
        (click)="fit.emit()">
        <ng-icon name="lucideMaximize" />
      </button>
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        aria-label="Vergrößern"
        (click)="zoomChange.emit(zoom() * 1.25)">
        <ng-icon name="lucideZoomIn" />
      </button>
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        [hlmTooltip]="'Originalgröße, ungefähr (abhängig vom Bildschirm)'"
        aria-label="Originalgröße"
        (click)="realSize.emit()">
        1:1
      </button>
    </div>

    <button
      hlmBtn
      size="sm"
      type="button"
      [variant]="longTexts() ? 'default' : 'outline'"
      [attr.aria-pressed]="longTexts()"
      [hlmTooltip]="'Name, Nachricht und Produkttexte in der längsten zu erwartenden Länge zeigen, auch in der Vorschau'"
      (click)="longTextsChange.emit(!longTexts())">
      <ng-icon name="lucideTextCursorInput" />
      Längste Texte
    </button>

    <button
      hlmBtn
      variant="outline"
      size="sm"
      type="button"
      [disabled]="!canSave() || previewing()"
      [hlmTooltip]="'Zuerst alle Pflichtelemente platzieren'"
      [tooltipDisabled]="canSave()"
      (click)="preview.emit()">
      @if (previewing()) {
        <hlm-spinner />
      } @else {
        <ng-icon name="lucideEye" />
      }
      Vorschau (PDF)
    </button>

    <app-save-button
      #saveButton
      size="sm"
      text="Layout speichern"
      [disabled]="!canSave() || !dirty()"
      [loading]="saving()"
      (clickEvent)="save.emit()" />

    <button
      hlmBtn
      size="sm"
      type="button"
      [disabled]="!canActivate()"
      [hlmTooltip]="activateHint()"
      [tooltipDisabled]="canActivate()"
      (click)="activate.emit()">
      <ng-icon name="lucideCircleCheck" />
      Verwenden
    </button>

    <button
      hlmBtn
      variant="outline"
      size="sm"
      type="button"
      aria-label="Weitere Aktionen"
      [disabled]="busy()"
      [hlmDropdownMenuTrigger]="moreMenu"
      align="end">
      <ng-icon name="lucideEllipsis" />
    </button>
  </div>
</div>

<ng-template #issuesMenu>
  <hlm-dropdown-menu class="w-96">
    @if (errorCount() > 0) {
      <hlm-dropdown-menu-label
        >Das Layout kann noch nicht gespeichert werden</hlm-dropdown-menu-label
      >
      <hlm-dropdown-menu-group>
        @for (issue of errors(); track $index) {
          <button
            hlmDropdownMenuItem
            class="text-destructive items-start whitespace-normal"
            [disabled]="!issue.elementId"
            (click)="issue.elementId && focusElement.emit(issue.elementId)">
            <ng-icon name="lucideTriangleAlert" class="mt-0.5 shrink-0" />
            <span>{{ issue.message }}</span>
          </button>
        }
      </hlm-dropdown-menu-group>
    }
    @if (errorCount() > 0 && warningCount() > 0) {
      <div hlmDropdownMenuSeparator></div>
    }
    @if (warningCount() > 0) {
      <hlm-dropdown-menu-label>Hinweise</hlm-dropdown-menu-label>
      <hlm-dropdown-menu-group>
        @for (issue of warnings(); track $index) {
          <button
            hlmDropdownMenuItem
            class="items-start whitespace-normal"
            [disabled]="!issue.elementId"
            (click)="issue.elementId && focusElement.emit(issue.elementId)">
            <ng-icon name="lucideInfo" class="mt-0.5 shrink-0" />
            <span>{{ issue.message }}</span>
          </button>
        }
      </hlm-dropdown-menu-group>
    }
    <div hlmDropdownMenuSeparator></div>
    <p class="text-muted-foreground px-2 py-1.5 text-xs">
      Ein Klick wählt das betroffene Element aus. Auf der Seite zeigt ein Abzeichen in der Ecke des
      Elements dasselbe.
    </p>
  </hlm-dropdown-menu>
</ng-template>

<ng-template #moreMenu>
  <hlm-dropdown-menu class="w-64">
    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem (click)="copy.emit(null)">
        <ng-icon name="lucideCopy" />
        Duplizieren
      </button>
      <button hlmDropdownMenuItem (click)="copy.emit(otherType())">
        <ng-icon name="lucideCopyPlus" />
        {{ copyAsLabel() }}
      </button>
    </hlm-dropdown-menu-group>
    <div hlmDropdownMenuSeparator></div>
    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem (click)="outlinesChange.emit(!showOutlines())">
        <ng-icon [name]="showOutlines() ? 'lucideCircleCheck' : 'lucideScan'" />
        Umrisse aller Elemente {{ showOutlines() ? 'ausblenden' : 'anzeigen' }}
      </button>
      <button hlmDropdownMenuItem (click)="shortcuts.emit()">
        <ng-icon name="lucideKeyboard" />
        Tastenkürzel
      </button>
    </hlm-dropdown-menu-group>
    <div hlmDropdownMenuSeparator></div>
    <a hlmDropdownMenuItem routerLink="/settings" [queryParams]="{ tab: 'voucher-design' }">
      <ng-icon name="lucideSettings" />
      Zu den Einstellungen
    </a>
  </hlm-dropdown-menu>
</ng-template>
